import { useState } from 'react'
import { useAnimateWhenVisible } from '../../../hooks/useAnimateWhenVisible'
import hero from './svg/01-hero-crowd-test.svg?raw'
import heroMinds from './svg/01-hero-two-hundred-minds.svg?raw'
import heroV4 from './svg/01-hero-change-of-mind.svg?raw'
import connect from './svg/02-setup-connect.svg?raw'
import plan from './svg/03-setup-plan.svg?raw'
import sync from './svg/04-setup-sync.svg?raw'
import products from './svg/05-capability-test-products.svg?raw'
import store from './svg/06-capability-test-store.svg?raw'
import productInsights from './svg/07-capability-product-insights.svg?raw'
import insights from './svg/08-capability-insights.svg?raw'
import opportunities from './svg/09-capability-opportunities.svg?raw'
import results from './svg/10-recent-tests-results.svg?raw'
import lock from './svg/11-private-by-design-lock.svg?raw'

const ART = {
  hero: heroMinds,
  heroDots: hero,
  heroV4,
  connect,
  plan,
  sync,
  products,
  store,
  productInsights,
  insights,
  opportunities,
  results,
  lock,
}
export type ArtName = keyof typeof ART

// Each SVG's own viewBox, read once at module load so an unmounted picture can still reserve its
// correct aspect ratio (no layout jump once it scrolls into view and actually loads).
const ASPECT: Record<ArtName, number> = Object.fromEntries(
  (Object.keys(ART) as ArtName[]).map((key) => {
    const match = ART[key].match(/viewBox="[^\s]+\s+[^\s]+\s+([\d.]+)\s+([\d.]+)"/)
    const [, w, h] = match ?? ['', '360', '240']
    return [key, Number(w) / Number(h)]
  }),
) as Record<ArtName, number>

/**
 * An animated App Home picture. Its SVG's own CSS animation starts the moment the picture mounts,
 * so this waits until the picture has scrolled into view to mount it — otherwise a one-shot "intro"
 * animation below the fold would already be finished by the time the merchant scrolls to it.
 *
 * `size` pins the rendered width in px (height follows, same aspect ratio — no stretching). Pass it
 * for slots that were a small fixed-size icon before this art (setup steps, the trust lock); omit it
 * to fill the slot's container width, matching the full-width card/hero slots.
 *
 * `maxWidth` caps a fill-width slot so it can't grow past a sensible size on a wide card (e.g. the
 * two big Test cards, which can stretch well past their artifact-review width on a wide screen).
 *
 * `background` is the fill behind the SVG's transparent areas — the warm card color for edge-to-edge
 * slots (the default), or 'transparent' for icons sitting directly on a white card (compact capability
 * cards).
 */
export function HomeArt({
  name,
  alt = '',
  replay = 0,
  size,
  maxWidth,
  center = false,
  background = '#F7F1E6',
}: {
  name: ArtName
  alt?: string
  replay?: number
  size?: number
  maxWidth?: number
  /** Centers a fixed-`size` picture in its slot. A plain flex wrapper, not margin:auto on the
   * picture itself — Shopify's s-grid/s-box apply their own padding and alignment to slotted
   * content inside their shadow DOM, which ignores margin/justify-self set on the slotted element. */
  center?: boolean
  background?: string
}) {
  const { ref, hasBeenVisible } = useAnimateWhenVisible<HTMLImageElement>()
  // Files are imported as text (Vite ?raw). A different comment makes a different URL,
  // so the browser builds a fresh image and the animation plays from the start.
  const src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(ART[name] + (replay ? `<!--${replay}-->` : ''))
  const picture = (
    <img
      ref={ref}
      key={replay}
      src={hasBeenVisible ? src : undefined}
      alt={alt}
      style={{
        display: 'block',
        // A grid/flex row stretches an unloaded (src-less) picture to match its tallest sibling —
        // aspect-ratio alone only wins that fight once there's real loaded content, so a fixed-size
        // picture gets its height spelled out in px too (a fill-width one keeps 'auto': its exact
        // pixel width isn't known until layout, same as before).
        width: size ? `${size}px` : '100%',
        height: size ? `${Math.round(size / ASPECT[name])}px` : 'auto',
        aspectRatio: String(ASPECT[name]),
        maxWidth: maxWidth ? `${maxWidth}px` : undefined,
        marginInline: maxWidth ? 'auto' : undefined,
        borderRadius: 8,
        background,
      }}
    />
  )
  return center ? <div style={{ display: 'flex', justifyContent: 'center' }}>{picture}</div> : picture
}

/** The hero with its replay button state. Wire `replay` to the Replay example button. */
export function useReplay() {
  const [n, setN] = useState(0)
  return [n, () => setN((x) => x + 1)] as const
}
